import { inject, Injectable, OnDestroy } from '@angular/core';

import { DsLoggingService } from '../../shared/services/ds-logging.service';
import { Clock } from '../clock/clock';
import { Disposable } from '../clock/interfaces';
import { durationMilliseconds, PlaybackService } from '../playback/playback.service';

// Deliberately far longer than any slide: a legitimately slow video must never be mistaken for a stall.
export const WATCHDOG_FLOOR_MS = 5 * 60 * 1000;
const CHECK_INTERVAL_MS = 30 * 1000;
const FORCED_ADVANCES_BEFORE_RESTART = 2;

// Last defence against a frozen board nobody is watching: stalled playback is nudged, then the runtime is restarted.
@Injectable()
export class WatchdogService implements OnDestroy {
  private readonly clock = inject(Clock);
  private readonly logging = inject(DsLoggingService);
  private readonly playback = inject(PlaybackService);
  private ticker?: Disposable;
  private restart?: () => void;
  private lastGeneration = -1;
  private lastChangeAt = 0;
  private forcedAdvances = 0;

  public start(restart: () => void): void {
    this.stop();
    this.restart = restart;
    this.lastGeneration = this.playback.generation();
    this.lastChangeAt = this.now();
    this.ticker = this.clock.every(CHECK_INTERVAL_MS, 0, () => this.check());
  }

  public stop(): void {
    this.ticker?.dispose();
    this.ticker = undefined;
    this.restart = undefined;
    this.forcedAdvances = 0;
  }

  public ngOnDestroy(): void {
    this.stop();
  }

  private check(): void {
    // Slide IDs repeat on a short loop, so progress is measured by presentations, not by what is on screen.
    const generation = this.playback.generation();

    if (generation !== this.lastGeneration) {
      this.lastGeneration = generation;
      this.lastChangeAt = this.now();
      this.forcedAdvances = 0;

      return;
    }

    const current = this.playback.current();

    if (!current || this.now() - this.lastChangeAt < this.deadline(current)) {
      return;
    }

    // The clock is reset before acting, so one stall cannot trip every deadline on the next tick.
    this.lastChangeAt = this.now();
    this.forcedAdvances++;
    this.logging.trackEvent('playback_stalled', {
      currentSlideKind: current.kind, stalledForMs: this.deadline(current), forcedAdvances: this.forcedAdvances
    });

    // Advancing twice changed nothing, so the fault is below playback and only a full restart clears it.
    if (this.forcedAdvances > FORCED_ADVANCES_BEFORE_RESTART) {
      this.forcedAdvances = 0;
      this.restart?.();

      return;
    }

    this.playback.advance(1);
  }

  private deadline(slide: NonNullable<ReturnType<PlaybackService['current']>>): number {
    return Math.max(3 * durationMilliseconds(slide), WATCHDOG_FLOOR_MS);
  }

  private now(): number {
    return this.clock.now().toEpochMilli();
  }
}
